import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject, Subscription, from, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, concatMap, catchError, finalize } from 'rxjs/operators';
import { ContainerStatus, ContainerType } from 'src/app/enum/enum';
import { LabelEntityType } from 'src/app/Services/enum';
import { Labels, LoginModel } from 'src/app/Models/ApplicationModels';
import { MasterDataSeriveService } from 'src/app/Services/master-data-serive.service';
import { ContainersService } from '../../Service/containers.service';
import { AdminBreadcrumbsService } from '../../../../admin-services/admin-breadcrumbs.service';
import { CustomToastsService } from 'src/app/Services/custom-toasts.service';
import { ErrorHandlerService } from 'src/app/Services/error-handler.service';
import { containerResponse } from '../../Model/Container';
import { AddUpdateContainerComponent } from '../add-update-container/add-update-container.component';
import { QzTrayService } from 'src/app/qz-tray.service';

@Component({
  selector: 'app-containers',
  templateUrl: './containers.component.html',
  styleUrls: ['./containers.component.css']
})
export class ContainersComponent implements OnInit, OnDestroy {
  @ViewChild(AddUpdateContainerComponent) addUpdateComponent!: AddUpdateContainerComponent;
  @ViewChild('labelFormatModalCloseBtn') labelFormatModalCloseBtn!: ElementRef;
  labelFormatModal: any;

  // Listing properties
  searchSubject: Subject<string> = new Subject<string>();
  searchSubscription!: Subscription;
  containerList: containerResponse[] = [];
  searchText: string = '';
  filterStatus?: ContainerStatus;
  filterType?: ContainerType;

  // Form/Dialog properties
  container: containerResponse = new containerResponse();
  containerToDelete: containerResponse | null = null;

  // User/Company/Store info
  userDetails: LoginModel = new LoginModel();
  CompanyId: number = 0;
  StoreId: number = 0;

  BinType = ContainerType;
  BinStatus = ContainerStatus;

  // Print properties
  labels: Array<Labels> = [];
  selectedLabelFormat: any | null = null;
  isPrinting: boolean = false;
  containerToPrint: containerResponse | null = null;

  constructor(
    private masterService: MasterDataSeriveService,
    private containersService: ContainersService,
    private adminBreadcrumbs: AdminBreadcrumbsService,
    private toastr: CustomToastsService,
    private errorService: ErrorHandlerService,
    private qzService: QzTrayService
  ) { }

  ngOnInit() {
    this.userDetails = this.masterService.getUserDetailsFromStorage();
    this.CompanyId = this.userDetails.companyID;
    this.StoreId = this.userDetails.storeId;

    this.setupBreadcrumbs();
    this.getAllContainers();

    // Initialize Bootstrap modal reference
    setTimeout(() => {
      const modalElement = document.getElementById('labelFormatModal');
      if (modalElement) {
        this.labelFormatModal = new (window as any).bootstrap.Modal(modalElement);
      }
    }, 0);

    // Setup search with debounce
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTextValue) => {
        this.searchText = searchTextValue;
        this.getAllContainers();
      });
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private setupBreadcrumbs(): void {
    this.adminBreadcrumbs.updateBreadcrumbs([
      { crumbName: 'Inventory Management', link: 'demo' },
      { crumbName: 'Production Bins', link: '/admin/home/containers' }
    ]);
  }

  // ============ LISTING METHODS ============

  getAllContainers() {
    this.containersService
      .getAllContainers(
        this.CompanyId,
        0,
        this.filterStatus,
        this.filterType,
        this.searchText
      )
      .subscribe({
        next: (response: any) => {
          this.containerList = response;
        },
        error: (error: any) => {
          this.errorService.getErrorMessage(error);
        }
      });
  }

  getStatusClass(statusVal: number | null | undefined): string {
    const status = ContainerStatus[statusVal as number];
    return status ? `status-${status}` : '';
  }

  getStatusLabel(statusVal: number | null | undefined): string {
    const status = ContainerStatus[statusVal as number];
    return status ? status.replace(/([A-Z])/g, ' $1').trim() : '';
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.searchSubject.next(value.trim());
  }

  resetListingForm(): void {
    this.container = new containerResponse();
    this.container.id = 0;
  }

  // ============ ADD/EDIT/DELETE METHODS ============

  onEditContainer(id: number): void {
    this.loadContainerById(id);
  }

  loadContainerById(id: number): void {
    this.containersService.getContainerById(id).subscribe({
      next: (response: any) => {
        this.container = response;
      },
      error: (error: any) => {
        this.errorService.getErrorMessage(error);
      }
    });
  }

  onDeleteContainer(container: containerResponse): void {
    this.containerToDelete = container;
  }

  confirmDelete(): void {
    if (this.containerToDelete) {
      this.containersService.deleteContainer(this.containerToDelete.id).subscribe({
        next: (response: any) => {
          this.toastr.success('Container deleted successfully');
          this.cancelDelete();
          this.getAllContainers();
        },
        error: (error: any) => {
          this.errorService.getErrorMessage(error);
        }
      });
    }
  }

  cancelDelete(): void {
    this.containerToDelete = null;
  }

  onPrintContainer(container: containerResponse): void {
    this.containerToPrint = container;
    this.selectedLabelFormat = null;
    this.getAllLabelFormats();
  }

  getAllLabelFormats(): void {
    this.masterService
      .getAllLabels(this.CompanyId)
      .subscribe({
        next: (res: any) => {
          console.log('All label formats:', res);
          const data: Labels[] = Array.isArray(res) ? res : [];
          // Filter labels where LabelEntityType is Container / Bin
          const binLabels = data.filter((l: Labels) =>
            l.labelEntityType === LabelEntityType.Container ||
            /bin/i.test(`${l.name || ''} ${l.description || ''}`)
          );

          // Use filtered bin labels, or fall back to all labels if none explicitly marked as Bin/Container
          this.labels = binLabels.length > 0 ? binLabels : data;

          // Auto-select single label format and print directly without showing modal
          if (this.labels.length === 1) {
            this.selectedLabelFormat = this.labels[0];
            this.isPrinting = true;
            this.printContainer();
          } else if (this.labels.length > 1) {
            // Show modal if there are multiple labels
            if (this.labelFormatModal) {
              this.labelFormatModal.show();
            }
          }
        },
        error: (err: any) => {
          this.toastr.error('Failed to load label formats');
          console.error('Error loading label formats:', err);
        }
      });
  }

  confirmPrint(): void {
    if (!this.selectedLabelFormat || !this.selectedLabelFormat.zplCode) {
      this.toastr.error('Please select a label format');
      return;
    }

    if (!this.containerToPrint) {
      this.toastr.error('No container selected for printing');
      return;
    }

    this.isPrinting = true;
    this.printContainer();
  }

  closeModalDialog(): void {
    if (this.labelFormatModal) {
      this.labelFormatModal.hide();
    }
    this.selectedLabelFormat = null;
    this.containerToPrint = null;
  }



  private getPrinterNameFromLabelFormat(labelFormat: Labels): string | null {
    const printerType = Number(
      labelFormat?.printerType ?? labelFormat?.printerType ?? 0
    );

    if (printerType === 1) return 'ZebraLabel';
    if (printerType === 2) return 'ZebraTag';
    if (printerType === 3) return 'ZebraBoth';

    return null;
  }

  private printContainer(): void {
    if (!this.selectedLabelFormat?.zplCode) {
      this.toastr.error('ZPL template missing');
      this.isPrinting = false;
      this.containerToPrint = null;
      return;
    }

    if (!this.containerToPrint) {
      this.toastr.error('No container to print');
      this.isPrinting = false;
      return;
    }

    const printerName = this.getPrinterNameFromLabelFormat(
      this.selectedLabelFormat
    );
    if (!printerName) {
      this.toastr.error('Printer type not configured for selected label');
      this.isPrinting = false;
      this.containerToPrint = null;
      return;
    }

    const zplTemplate = this.selectedLabelFormat.zplCode;
    const userDetails = this.masterService.getUserDetailsFromStorage();
    const companyName = userDetails.company || '';
    const container = this.containerToPrint;

    // Replace placeholders in ZPL template
    const finalZpl = zplTemplate
      .replace(/\{{1,2}CompanyName\}{1,2}/g, companyName)
      .replace(/\{{1,2}ReferenceNumber\}{1,2}/g, container.referenceNumber || '')
      .replace(/\{{1,2}Description\}{1,2}/g, container.description || '')
      .replace(/\{{1,2}BarcodeValue\}{1,2}/g, container.externalId || '')
      .replace(/\{{1,2}BinId\}{1,2}/g, container.externalId || '');

    this.qzService.printZpl(printerName, finalZpl).pipe(
      finalize(() => {
        this.isPrinting = false;
        this.containerToPrint = null;
        this.labelFormatModalCloseBtn.nativeElement.click();
      }),
      catchError((err: any) => {
        this.toastr.error('Failed to print label');
        console.error('Print error:', err);
        return of(null);
      })
    ).subscribe(() => {
      this.toastr.success('Container label printed successfully');
    });
  }

  onContainerSaved(): void {
    this.getAllContainers();
    this.resetListingForm();
  }

  onResetForm(): void {
    this.resetListingForm();
  }
}

